import React, { useState } from "react";
import { RobotIcon, SendIcon, DesktopIcon, MobileIcon, CloseIcon } from "../Auth/onboarding/icons";
import { MinusIcon } from "../Dashboard/icons";
import { readWidgetTheme } from "../../utils/widgetTheme";
import LauncherGlyph from "../Customization/LauncherGlyph";
import { isLucideIcon } from "../Customization/launcherIcons";
import { alignClass, placementStyles } from "../Customization/widgetPlacement";
import CardHeading from "./CardHeading";

export default function PreviewCard({ assistant }) {
  const [device, setDevice] = useState("desktop");

  // Flat theme off the assistant record, whichever shape the API sent.
  const theme = readWidgetTheme(assistant);

  const name = assistant?.name || "Hostor";
  const greeting =
    theme.welcomeMessage ||
    assistant?.welcomeMessage ||
    `Hi! I'm ${name} 👋 How can I help you today?`;

  // Matches the widget's own default, so an assistant with no saved theme
  // still previews sensibly rather than collapsing to square corners.
  const radius = typeof theme.borderRadius === "number" ? theme.borderRadius : 12;
  const headerColor = theme.headerColor || theme.primaryColor;
  const prompts = theme.suggestedPrompts?.length
    ? theme.suggestedPrompts.slice(0, 3)
    : [];

  // An uploaded image wins, then a picked icon, then the default robot.
  const avatar = theme.avatarUrl ? (
    <img src={theme.avatarUrl} alt="" />
  ) : isLucideIcon(theme.launcherStyle) ? (
    <LauncherGlyph value={theme.launcherStyle} fallback={<RobotIcon />} />
  ) : (
    <RobotIcon />
  );

  // The image is the avatar in full; the tint only backs a glyph.
  const avatarBg = theme.avatarUrl ? { background: "none" } : undefined;

  const align = alignClass(theme.position);

  // No launcher bubble is drawn on this card, so the panel's own corner sits
  // on the stored point rather than clearing a bubble that isn't there.
  const { panel: panelStyle } = placementStyles(
    theme.position,
    { x: theme.positionOffsetX, y: theme.positionOffsetY },
    { launcher: 0, gap: 0 },
  );

  return (
    <section className="dash-card">
      <CardHeading
        step={2}
        tone="purple"
        title="Preview"
        subtitle="See how Hostor looks on your site."
      />

      <div
        className={`cz-preview-stage ist-preview-stage ${align}${device === "mobile" ? " is-mobile" : ""}`}
      >
        <div className="onboarding__browser">
          <div className="onboarding__browser-bar">
            <span className="onboarding__dot onboarding__dot--red" />
            <span className="onboarding__dot onboarding__dot--yellow" />
            <span className="onboarding__dot onboarding__dot--green" />
          </div>
          <div className="onboarding__browser-body">
            <div className="onboarding__browser-lines">
              <span />
              <span />
              <span />
            </div>
            <div className="onboarding__browser-tiles">
              <div />
              <div />
              <div />
            </div>
          </div>
        </div>

        <div
          className="cz-floating-widget ist-floating-widget review-chat"
          style={{ borderRadius: `${radius}px`, ...panelStyle }}
        >
          <div
            className="review-chat-header"
            style={headerColor ? { background: headerColor } : undefined}
          >
            <span className="review-chat-avatar" style={avatarBg}>
              {avatar}
            </span>
            <div className="review-chat-heading">
              <strong>{name}</strong>
              {theme.showOnlineStatus !== false && (
                <span className="review-chat-status">
                  <span className="review-chat-status-dot" />
                  Online
                </span>
              )}
            </div>
            <span className="review-chat-kebab">
              <MinusIcon />
            </span>
          </div>

          <div className="review-chat-body">
            {theme.showWelcomeMessage !== false && (
              <div className="review-chat-message">
                <span
                  className="review-chat-message-avatar"
                  style={
                    avatarBg ||
                    (theme.accentColor
                      ? { background: theme.accentColor }
                      : undefined)
                  }
                >
                  {avatar}
                </span>
                <p
                  className="review-chat-bubble"
                  style={{
                    borderRadius: `${radius}px ${radius}px ${radius}px 2px`,
                  }}
                >
                  {greeting}
                </p>
              </div>
            )}

            {prompts.length > 0 && (
              <div className="review-chat-suggestions">
                {prompts.map((prompt) => (
                  <button
                    type="button"
                    key={prompt}
                    style={{
                      borderColor: theme.accentColor,
                      color: theme.buttonColor || theme.primaryColor,
                    }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="review-chat-input">
            <input type="text" placeholder="Type your message..." disabled />
            <span
              className="review-chat-send"
              style={
                theme.buttonColor ? { background: theme.buttonColor } : undefined
              }
            >
              <SendIcon />
            </span>
          </div>

          {theme.showPoweredBy !== false && (
            <div className="cz-widget-footer">
              Powered by{" "}
              <img src="/assets/logo.svg" alt="Hostor" className="widgetLogo" />
            </div>
          )}
        </div>
      </div>

      <div className="review-device-toggle ist-device-toggle">
        <button type="button" className={device === "desktop" ? "is-active" : ""} onClick={() => setDevice("desktop")}>
          <DesktopIcon />
          Desktop
        </button>
        <button type="button" className={device === "mobile" ? "is-active" : ""} onClick={() => setDevice("mobile")}>
          <MobileIcon />
          Mobile
        </button>
      </div>
    </section>
  );
}
